import React, { memo, useEffect, useState } from 'react';
import { Drawer, DrawerHeader, DrawerBody, DrawerFooter, Button, Alert } from '@one-thd/sui-atomic-components';
import { FacetGroup as FacetGroupModel, FacetSelections } from '../types';
import { FacetGroup } from './FacetGroup';

export interface FilterDrawerProps {
  open: boolean;
  onClose: () => void;
  facetGroups: FacetGroupModel[];
  onApply: (selections: FacetSelections) => void;
  isLoading?: boolean;
  error?: boolean;
}

const seedSelections = (facetGroups: FacetGroupModel[]): FacetSelections =>
  facetGroups.reduce<FacetSelections>((acc, group) => {
    acc[group.key] = [...group.selectedValues];
    return acc;
  }, {});

const hasChanges = (seeded: FacetSelections, pending: FacetSelections): boolean =>
  Object.keys(pending).some((key) => {
    const seededValues = seeded[key] ?? [];
    const pendingValues = pending[key] ?? [];
    if (seededValues.length !== pendingValues.length) {
      return true;
    }
    return pendingValues.some((value) => !seededValues.includes(value));
  });

const FilterDrawerComponent: React.FC<FilterDrawerProps> = memo(
  ({ open, onClose, facetGroups, onApply, isLoading = false, error = false }: FilterDrawerProps) => {
    useEffect(() => {
      LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('filter-drawer.ready');
    }, []);

    const [seededSelections, setSeededSelections] = useState<FacetSelections>(() => seedSelections(facetGroups));
    const [pendingSelections, setPendingSelections] = useState<FacetSelections>(() => seedSelections(facetGroups));

    useEffect(() => {
      if (open) {
        const seeded = seedSelections(facetGroups);
        setSeededSelections(seeded);
        setPendingSelections(seeded);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const toggleOption = (group: FacetGroupModel, value: string) => {
      setPendingSelections((previous) => {
        if (group.selectionMode === 'single') {
          return { ...previous, [group.key]: [value] };
        }
        const current = previous[group.key] ?? [];
        const next = current.includes(value) ? current.filter((existing) => existing !== value) : [...current, value];
        return { ...previous, [group.key]: next };
      });
    };

    const canViewResults = hasChanges(seededSelections, pendingSelections);

    const handleViewResults = () => {
      onApply(pendingSelections);
      onClose();
    };

    return (
      <Drawer open={open} onClose={onClose} role="dialog" aria-modal="true" aria-label="Filter">
        <DrawerHeader title="Filter" onClose={onClose} />
        <DrawerBody>
          {error ? (
            <Alert status="error">We were unable to retrieve your filter options. Refresh the page and try again.</Alert>
          ) : (
            facetGroups.map((group) => (
              <FacetGroup
                key={group.key}
                group={group}
                selectedValues={pendingSelections[group.key] ?? []}
                onToggleOption={(value) => toggleOption(group, value)}
                isLoading={isLoading}
              />
            ))
          )}
        </DrawerBody>
        <DrawerFooter>
          <Button variant="primary" fullWidth disabled={!canViewResults} onClick={handleViewResults}>
            View Results
          </Button>
        </DrawerFooter>
      </Drawer>
    );
  }
);

FilterDrawerComponent.displayName = 'FilterDrawer';

const FilterDrawer = FilterDrawerComponent;

export { FilterDrawer };
